import { Head, Link, router } from "@inertiajs/react"
import { Disc, Edit, Trash2, Music2, UserRound, ChevronRight } from "lucide-react"
import { PublicShell } from "@/components/PublicShell"
import { RequestDeletionButton } from "@/components/RequestDeletionButton"

type PrimaryImage = { url: string; alt_text: string }

type RecordingRow = {
  id: number
  title: string
  slug: string
  recording_type: string | null
  released_year: number | null
  recordable_name: string | null
  recordable_slug: string | null
  recordable_type: string
  primary_image: PrimaryImage | null
}

type RecordLabelDetail = {
  id: number
  name: string
  slug: string
  content: string | null
  recordings: RecordingRow[]
}

type Permissions = { can_edit: boolean; can_delete: boolean; can_request_deletion: boolean }

type Props = {
  record_label: RecordLabelDetail
  permissions: Permissions
}

function RecordingTypeTag({ type }: { type: string | null }) {
  if (!type) return null
  return (
    <span
      className="text-xs text-stone-400 dark:text-stone-500 capitalize"
      style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
    >
      {type}
    </span>
  )
}

function RecordableIcon({ type }: { type: string }) {
  return type === "Band"
    ? <Music2 className="w-3.5 h-3.5 text-stone-400" />
    : <UserRound className="w-3.5 h-3.5 text-stone-400" />
}

export default function RecordLabelShow({ record_label, permissions }: Props) {
  const returnTo = `/record_labels/${record_label.slug}`

  function handleDelete() {
    if (confirm(`Delete "${record_label.name}"? This cannot be undone.`)) {
      router.delete(`/record_labels/${record_label.slug}`)
    }
  }

  const metaDescription = record_label.content
    ? record_label.content.replace(/<[^>]*>/g, "").slice(0, 155)
    : `${record_label.name} — record label with ${record_label.recordings.length} recording${record_label.recordings.length !== 1 ? "s" : ""} in the Australian Music Database.`

  return (
    <>
      <Head title={record_label.name}>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={record_label.name} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
      </Head>
      <PublicShell>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          {/* Header */}
          <div className="flex items-start gap-4 mb-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Disc className="w-5 h-5 text-stone-400 dark:text-stone-500 shrink-0" />
                <span
                  className="text-xs text-stone-400 dark:text-stone-500 uppercase tracking-wider"
                  style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
                >
                  Record Label
                </span>
              </div>
              <h1>{record_label.name}</h1>
            </div>
            {permissions.can_edit && (
              <div className="flex items-center gap-2 shrink-0 pt-1">
                <Link
                  href={`/record_labels/${record_label.slug}/edit?return_to=${encodeURIComponent(returnTo)}`}
                  className="inline-flex items-center gap-1 text-sm text-stone-400 hover:text-amber-600 dark:hover:text-amber-400 no-underline transition-colors"
                >
                  <Edit className="w-3.5 h-3.5" /> Edit
                </Link>
                {permissions.can_delete && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="inline-flex items-center gap-1 text-sm text-stone-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                )}
                {permissions.can_request_deletion && (
                  <RequestDeletionButton type="RecordLabel" id={record_label.id} name={record_label.name} />
                )}
              </div>
            )}
          </div>

          {/* Content / Description */}
          {record_label.content && (
            <div
              className="mt-4 mb-8 prose prose-stone dark:prose-invert max-w-none text-sm"
              dangerouslySetInnerHTML={{ __html: record_label.content }}
            />
          )}

          {/* Recordings */}
          <div className="mt-8">
            <h2 className="mb-4">
              Recordings
              <span
                className="ml-2 text-sm font-normal text-stone-400 dark:text-stone-500"
                style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
              >
                {record_label.recordings.length}
              </span>
            </h2>

            {record_label.recordings.length === 0 ? (
              <p className="text-sm text-stone-500 dark:text-stone-400 py-4">No recordings on this label yet.</p>
            ) : (
              <ul className="divide-y divide-stone-100 dark:divide-stone-800 rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 overflow-hidden">
                {record_label.recordings.map((rec) => (
                  <li key={rec.id}>
                    <Link
                      href={`/recordings/${rec.slug}`}
                      className="flex items-center gap-3 px-4 py-3 no-underline hover:bg-stone-50 dark:hover:bg-stone-800/60 transition-colors group"
                    >
                      {/* Thumbnail */}
                      <div className="w-10 h-10 rounded-md bg-stone-100 dark:bg-stone-800 shrink-0 overflow-hidden flex items-center justify-center">
                        {rec.primary_image ? (
                          <img
                            src={rec.primary_image.url}
                            alt={rec.primary_image.alt_text}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <Disc className="w-5 h-5 text-stone-300 dark:text-stone-600" />
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div
                          className="text-sm font-medium text-stone-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors truncate"
                          style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
                        >
                          {rec.title}
                        </div>
                        {rec.recordable_name && (
                          <div className="flex items-center gap-1 mt-0.5">
                            <RecordableIcon type={rec.recordable_type} />
                            <span
                              className="text-xs text-stone-400 dark:text-stone-500 truncate"
                              style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
                            >
                              {rec.recordable_name}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Year + type */}
                      <div className="flex items-center gap-2 shrink-0">
                        {rec.released_year && (
                          <span
                            className="text-xs text-stone-400 dark:text-stone-500"
                            style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
                          >
                            {rec.released_year}
                          </span>
                        )}
                        <RecordingTypeTag type={rec.recording_type} />
                        <ChevronRight className="w-4 h-4 text-stone-300 dark:text-stone-600 group-hover:text-amber-400 transition-colors" />
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </PublicShell>
    </>
  )
}
